import { AdminSortOrder, GetAdminUserAppEventsResponse } from '@orbit/api-types'
import api from '@lib/api'
import { queryKeys } from '@lib/queryKeys'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

interface UseAdminUserAppEventsParams {
	userId: number
	page: number
	pageSize?: number
	/** Timestamp ordering — `desc` (newest first) is what the server defaults to. */
	sort: AdminSortOrder
}

/**
 * Fetches a page of one user's timeline (GET /v1/admin/users/:id/app-events): their
 * app events merged with the records they created in the domain tables.
 * Deliberately ignores the global admin date range — this is the user's whole history
 * — so only the id, page, and sort direction are part of the query key. Disabled until
 * the id is a valid positive number, and previous data is kept while the next page or
 * a flipped sort loads. Gated server-side by AdminGuard.
 */
export const useAdminUserAppEvents = ({ userId, page, pageSize, sort }: UseAdminUserAppEventsParams) => {
	return useQuery<GetAdminUserAppEventsResponse>({
		queryKey: queryKeys.adminUserAppEvents(userId, page, pageSize, sort),
		queryFn: async () => {
			const response = await api.get<GetAdminUserAppEventsResponse>(
				`/admin/users/${userId}/app-events`,
				{ params: { page, pageSize, sort } },
			)
			return response.data
		},
		enabled: Number.isFinite(userId) && userId > 0,
		placeholderData: keepPreviousData,
	})
}
